/**
 * BusinessPanel — Oferta / Panel firm, 3-kolumnowy layout
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Edit, Eye, Trash2, ToggleLeft, ToggleRight, Briefcase, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import AssistantPanel from '@/components/AssistantPanel';
import { API_BASE as API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function BusinessPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${API}/api/business/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; return []; } return r.json(); })
      .then(data => setBusinesses(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Blad ladowania'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac te firme?')) return;
    try {
      await fetch(`${API}/api/business/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setBusinesses(prev => prev.filter(b => b.id !== id));
      toast.success('Firma usunieta');
    } catch { toast.error('Blad usuwania'); }
  };

  const togglePublish = async (biz) => {
    try {
      const res = await fetch(`${API}/api/business/${biz.id}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ is_published: !biz.is_published }) });
      const updated = await res.json();
      setBusinesses(prev => prev.map(b => b.id === biz.id ? { ...b, is_published: updated.is_published } : b));
      toast.success(updated.is_published ? 'Strona opublikowana' : 'Strona ukryta');
    } catch { toast.error('Blad'); }
  };

  return (
    <PanelLayout>
      <div className="max-w-[1200px] mx-auto" data-testid="business-panel">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-bold text-zinc-900">Oferta</h1>
            <p className="text-[14px] text-zinc-500">Zarządzaj profilami firm i usługami.</p>
          </div>
          <Button onClick={() => navigate('/panel/profil')} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl" data-testid="create-business-btn">
            <Plus className="w-4 h-4 mr-2" /> Uzupełnij profil
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8" data-testid="business-content">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#F47A3B]" /></div>
            ) : businesses.length === 0 ? (
              <div className="bg-white rounded-2xl border border-zinc-200 p-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="empty-state">
                <div className="w-14 h-14 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-5">
                  <Briefcase className="w-7 h-7 text-[#F47A3B]" />
                </div>
                <h2 className="text-[18px] font-bold text-zinc-900 mb-2">Nie masz jeszcze oferty</h2>
                <p className="text-[14px] text-zinc-500 leading-relaxed mb-2 max-w-md mx-auto">Dodaj swoją firmę i usługi, aby klienci mogli Cię znaleźć online.</p>
                <p className="text-[13px] text-zinc-400 italic mb-5 max-w-md mx-auto">Firmy z pełną ofertą i zdjęciami mają więcej zapytań.</p>
                <Button onClick={() => navigate('/panel/profil')} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl px-6">
                  <Plus className="w-4 h-4 mr-2" /> Uzupełnij w profilu
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {businesses.map(biz => (
                  <div key={biz.id} className="bg-white rounded-2xl border border-zinc-200 p-5 hover:shadow-md transition-shadow shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`business-card-${biz.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-[16px] font-bold text-zinc-900">{biz.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${biz.is_published ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-500'}`}>
                            {biz.is_published ? 'Opublikowana' : 'Szkic'}
                          </span>
                        </div>
                        <p className="text-[13px] text-zinc-500">{biz.industry}{biz.city ? ` — ${biz.city}` : ''}</p>
                        {biz.services?.length > 0 && <p className="text-[12px] text-zinc-400 mt-1">{biz.services.length} usług</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        {biz.is_published && (
                          <a href={`/firma/${biz.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-zinc-400 hover:text-zinc-900 rounded-lg hover:bg-[#F3F4F6] transition-colors">
                            <Eye className="w-4 h-4" />
                          </a>
                        )}
                        <button onClick={() => navigate('/panel/profil')} className="p-2 text-zinc-400 hover:text-zinc-900 rounded-lg hover:bg-[#F3F4F6] transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => togglePublish(biz)} className="p-2 text-zinc-400 hover:text-zinc-900 rounded-lg hover:bg-[#F3F4F6] transition-colors">
                          {biz.is_published ? <ToggleRight className="w-4 h-4 text-[#34A853]" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                        <button onClick={() => handleDelete(biz.id)} className="p-2 text-zinc-400 hover:text-[#EA4335] rounded-lg hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN */}
          <div className="lg:col-span-4" data-testid="business-right-column">
            <AssistantPanel
              whatNow="Dodaj usługi, cennik i zdjęcia realizacji. Im więcej szczegółów, tym łatwiej klienci podejmą decyzję."
            />
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}
